import { AddImmunizationDialog } from '@/components/molecules/AddImmunizationDialog';
import { RecordListScroll } from '@liza/ui/components/RecordListScroll';
import { useInfiniteRecordList } from '@liza/ui/hooks/useInfiniteRecordList';
import { useTRPC, useTRPCClient } from '@/utils/trpc';
import type { PatientAdminApiRouterOutputs } from '@liza/patient-admin-api';
import { ImmunizationsList } from '@liza/ui/components/records/ImmunizationsList';
import {
  type RecordsListFilters,
  defaultFilters,
  RecordsListControls,
} from '@liza/ui/components/records/RecordsListControls';
import { useRecordPromotionStatus } from '@liza/ui/hooks/useRecordPromotionStatus';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebouncedCallback } from 'use-debounce';
import type { DetailPanelView } from '../types';

type Patient = PatientAdminApiRouterOutputs['patient']['get']['patient'];

type Props = {
  onViewChange: (view: DetailPanelView | null) => void;
  patient: Patient;
  selectedId?: string;
};

export const Immunizations = ({ onViewChange, patient, selectedId }: Props): React.ReactElement => {
  const { t } = useTranslation('records');
  const trpc = useTRPC();
  const trpcClient = useTRPCClient();
  const queryClient = useQueryClient();

  const [filters, setFilters] = useState<RecordsListFilters>(defaultFilters);
  const handleFiltersChange = useDebouncedCallback(setFilters, 300);
  const [isAddOpen, setIsAddOpen] = useState(false);

  const listOptions = {
    ownership: filters.ownership,
    searchParams: { 'vaccine-code:text': filters.searchQuery },
    status: filters.status,
  };

  const {
    items: immunizations,
    query,
    bottomSentinelRef,
  } = useInfiniteRecordList({
    queryKey: trpc.patient.immunization.list.queryKey({ medplumPatientId: patient.medplumId, options: listOptions }),
    queryFn: async ({ offset, count }) => {
      const res = await trpcClient.patient.immunization.list.query({
        medplumPatientId: patient.medplumId,
        options: listOptions,
        offset,
        count,
      });
      return { items: res.immunizations, hasMore: res.hasMore };
    },
  });

  const { mutateAsync: createImmunization } = useMutation(
    trpc.patient.immunization.create.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: trpc.patient.immunization.list.queryKey({ medplumPatientId: patient.medplumId }),
        });
      },
    }),
  );

  const { promote, getPromotionStatus } = useRecordPromotionStatus();

  const handlePromotionClick = useCallback(
    (id: string): void => {
      const source = immunizations.find((i) => i.id === id);
      if (!source) return;

      void promote(id, async () =>
        createImmunization({
          resource: {
            ...source,
            id: undefined,
            patient: { reference: `Patient/${patient.medplumId}` },
            recorded: new Date().toISOString(),
          },
        }),
      );
    },
    [createImmunization, immunizations, patient, promote],
  );

  return (
    <div className="flex flex-col gap-2 p-2">
      <RecordsListControls
        title={t('immunizations')}
        searchPlaceholder={t('searchImmunizations')}
        onFiltersChange={handleFiltersChange}
        onAdd={() => setIsAddOpen(true)}
        addLabel={t('addImmunization')}
      />
      <AddImmunizationDialog medplumPatientId={patient.medplumId} isOpen={isAddOpen} onOpenChange={setIsAddOpen} />
      <RecordListScroll
        isLoading={query.isLoading}
        isError={query.isError}
        error={query.error}
        isFetchingNextPage={query.isFetchingNextPage}
        bottomSentinelRef={bottomSentinelRef}
      >
        <ImmunizationsList
          immunizations={immunizations}
          onSelectImmunization={(id) => onViewChange({ type: 'immunization-detail', id })}
          selectedImmunizationId={selectedId}
          onPromotionClick={filters.ownership === 'external' ? handlePromotionClick : undefined}
          getPromotionStatus={getPromotionStatus}
        />
      </RecordListScroll>
    </div>
  );
};
